import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Box, Button, Radio, FormControlLabel, Checkbox, Alert, TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import "moment/locale/th";
import { formatNumber } from "../../../utils/formatNumber";

export default function SOSelectionDialog({
  open, onClose, soSelect = [], setSoSelect, soItems = [],
  insuranceEnabled = false, lockItems = [], lockSelect = [], setLockSelect,
  locksLoading = false, locksError = "", readOnly = false,
}) {
  const [selectedRadio, setSelectedRadio] = useState("none");
  const [checkedItems, setCheckedItems] = useState([]);
  const [lockStartDate, setLockStartDate] = useState("");
  const [lockEndDate, setLockEndDate] = useState("");
  const maxSelect = 10;

  useEffect(() => {
    if (!open) return;
    const locksSelected = lockSelect.length > 0;
    setSelectedRadio(locksSelected ? "insurance" : soSelect.length ? "so" : "none");
    setCheckedItems((locksSelected ? lockSelect : soSelect).map((item) => item.id));
    if (locksSelected) {
      const dates = lockSelect
        .map((item) => moment(item.recordDate || item.master?.recordDate).format("YYYY-MM-DD"))
        .filter(Boolean)
        .sort();
      setLockStartDate(dates[0] || "");
      setLockEndDate(dates[dates.length - 1] || dates[0] || "");
    }
  }, [open, soSelect, lockSelect]);

  useEffect(() => {
    if (!open || lockSelect.length > 0 || lockItems.length === 0) return;
    const dates = lockItems
      .map((item) => moment(item.recordDate || item.master?.recordDate).format("YYYY-MM-DD"))
      .filter(Boolean)
      .sort();
    setLockStartDate(dates[0] || "");
    setLockEndDate(dates[0] || "");
  }, [open, lockItems, lockSelect]);

  const chooseType = (type) => {
    setSelectedRadio(type);
    setCheckedItems(type === "insurance" ? selectedLockRows.map((row) => row.id) : []);
  };
  const date = (value) => value ? moment(value).add(543, "year").format("D MMM YYYY") : "-";
  const soRows = soItems.map((order) => ({
    id: order.id,
    orderNumber: order.soNumber
      ? String(order.soNumber).split("-").pop()
      : String(order.id).padStart(3, "0"),
    detail: order.detail || "-", quantity: `${order.items?.length ?? 0} ไอเท็ม`,
    startDate: date(order.startDate), endDate: date(order.endDate),
  }));
  const lockRows = useMemo(
    () => lockItems.map((lock) => ({
      ...lock,
      productName: lock.sellProduct?.title ?? lock.product?.title ?? "-",
      recordDateValue: lock.recordDate || lock.master?.recordDate,
      recordDate: date(lock.recordDate || lock.master?.recordDate),
    })),
    [lockItems],
  );
  const selectedLockRows = useMemo(
    () => lockRows.filter((row) => {
      const date = moment(row.recordDateValue).format("YYYY-MM-DD");
      return (
        (!lockStartDate || date >= lockStartDate) &&
        (!lockEndDate || date <= lockEndDate)
      );
    }),
    [lockRows, lockStartDate, lockEndDate],
  );
  const rows = selectedRadio === "insurance" ? selectedLockRows : soRows;

  useEffect(() => {
    if (selectedRadio === "insurance") {
      setCheckedItems(selectedLockRows.map((row) => row.id));
    }
  }, [selectedRadio, selectedLockRows]);
  const checkboxColumn = {
    field: "checkbox", headerName: "", width: 50, sortable: false, disableColumnMenu: true,
    renderHeader: () => (
      <Checkbox
        disabled={readOnly}
        checked={rows.length > 0 && rows.slice(0, maxSelect).every((row) => checkedItems.includes(row.id))}
        indeterminate={checkedItems.length > 0 && !rows.slice(0, maxSelect).every((row) => checkedItems.includes(row.id))}
        onChange={(event) => setCheckedItems(event.target.checked ? rows.slice(0, maxSelect).map((row) => row.id) : [])}
      />
    ),
    renderCell: ({ row }) => (
      <Checkbox
        disabled={readOnly || (!checkedItems.includes(row.id) && checkedItems.length >= maxSelect)}
        checked={checkedItems.includes(row.id)}
        onChange={() => setCheckedItems((ids) => ids.includes(row.id) ? ids.filter((id) => id !== row.id) : [...ids, row.id])}
      />
    ),
  };
  const columns = selectedRadio === "insurance" ? [
    checkboxColumn,
    { field: "id", headerName: "Lock #", width: 85 },
    { field: "recordDate", headerName: "วันที่ล็อคราคา", width: 130 },
    { field: "productName", headerName: "ขายเป็นสินค้า", flex: 1, minWidth: 180 },
    { field: "specialPrice", headerName: "ราคาพิเศษ ++ (บาท/กก.)", width: 185, renderCell: ({ value }) => formatNumber(value) },
    { field: "saleRemainingWeight", headerName: "น้ำหนักที่ขายได้ (กก.)", width: 180, renderCell: ({ value }) => formatNumber(value) },
  ] : [
    checkboxColumn,
    { field: "orderNumber", headerName: "SO #", width: 100 },
    { field: "detail", headerName: "รายละเอียด", width: 300 },
    { field: "quantity", headerName: "จำนวนสินค้า", width: 130 },
    { field: "startDate", headerName: "เริ่มต้น", width: 120 },
    { field: "endDate", headerName: "สิ้นสุด", width: 120 },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>การเลือกราคาสินค้า</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>กรุณาเลือกประเภทราคาสินค้าถ้าต้องการใช้กับตั๋วนี้</Typography>
        {[
          ["so", "ใบราคาสินค้า (SO)"],
          ...((insuranceEnabled || lockSelect.length) ? [["insurance", "ประกันราคาของสาขา"]] : []),
          ["none", "ไม่ใช้การล็อกราคา (ทั่วไป)"],
        ].map(([type, label]) => (
          <Box key={type} sx={{ mb: 2 }}>
            <FormControlLabel
              disabled={readOnly}
              control={<Radio checked={selectedRadio === type} onChange={() => chooseType(type)} />}
              label={`${label}${selectedRadio === "so" && type === "so" ? ` — ${checkedItems.length}/${maxSelect}` : ""}`}
            />
            {selectedRadio === type && type !== "none" && (
              <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1 }}>
                {type === "insurance" ? (
                  <>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        label="วันที่เริ่มต้น"
                        type="date"
                        size="small"
                        value={lockStartDate}
                        onChange={(event) => setLockStartDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: lockEndDate || undefined }}
                      />
                      <TextField
                        label="วันที่สิ้นสุด"
                        type="date"
                        size="small"
                        value={lockEndDate}
                        onChange={(event) => setLockEndDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: lockStartDate || undefined }}
                      />
                    </Box>
                    {locksError && <Alert severity="error" sx={{ mt: 1 }}>{locksError}</Alert>}
                  </>
                ) : (
                  <DataGrid rows={rows} columns={columns} autoHeight disableRowSelectionOnClick hideFooter
                    localeText={{ noRowsLabel: "ไม่มีรายการที่เลือกใช้ได้" }} />
                )}
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <Button fullWidth variant="contained"
          disabled={readOnly || (selectedRadio === "insurance" && (locksLoading || Boolean(locksError) || !selectedLockRows.length))}
          onClick={() => {
            setSoSelect(selectedRadio === "so" ? soItems.filter((item) => checkedItems.includes(item.id)) : []);
            setLockSelect?.(selectedRadio === "insurance" ? selectedLockRows : []);
            onClose();
          }}
          sx={{ borderRadius: 0, py: 1.8, fontWeight: 700, bgcolor: "#1E2630", "&:hover": { bgcolor: "#111820" } }}>
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
